import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { hasPermission } from '../../rbac';
import { businessAPI } from '../../services/api';
import BusinessDetailModal from './BusinessDetailModal';
import useBackButtonClose from '../../hooks/useBackButtonClose';
import {
  BUSINESS_TYPES, STATUS_LABELS, TYPE_LABELS, errorMessage,
} from './businessRegistryUi';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import './AdminList.css';
import './AdminBusinessesList.css';

const STATUS_FILTERS = ['draft', 'pending', 'verified', 'rejected', 'suspended', 'archived'];

export default function AdminBusinessesList() {
  const { tenantSlug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canCreate = hasPermission(user, 'businesses', 'create');
  const canUpdate = hasPermission(user, 'businesses', 'update');
  const canVerify = hasPermission(user, 'businesses', 'verify');
  const canManageUsers = hasPermission(user, 'businesses', 'manage_users');
  const canArchive = hasPermission(user, 'businesses', 'archive') || hasPermission(user, 'businesses', 'delete');
  const canManage = canUpdate || canVerify || canManageUsers || canArchive;

  const [businesses, setBusinesses] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 20, totalPages: 1 });
  const [filters, setFilters] = useState({ q: '', type: '', status: '', tax_readiness: '' });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [archivingId, setArchivingId] = useState(null);
  const [approvingId, setApprovingId] = useState(null);

  useBackButtonClose(Boolean(selectedBusiness), () => setSelectedBusiness(null));

  const updateFilter = (name, value) => {
    setFilters(current => ({ ...current, [name]: value }));
    setPage(1);
  };

  const fetchBusinesses = useCallback(async (cancelled) => {
    setLoading(true);
    setError('');
    try {
      const response = await businessAPI.getAdmin(tenantSlug, {
        page,
        limit: pageSize,
        q: filters.q.trim() || undefined,
        type: filters.type || undefined,
        status: filters.status || undefined,
        tax_readiness: filters.tax_readiness || undefined,
      });
      if (cancelled()) return;
      setBusinesses(response.data.businesses || []);
      setPagination(response.data.pagination || { total: 0, page, limit: pageSize, totalPages: 0 });
    } catch (requestError) {
      if (!cancelled()) setError(errorMessage(requestError, 'व्यवसाय नोंदणी लोड करता आली नाही.'));
    } finally {
      if (!cancelled()) setLoading(false);
    }
  }, [filters, page, pageSize, tenantSlug]);

  useEffect(() => {
    let inactive = false;
    const timer = window.setTimeout(() => fetchBusinesses(() => inactive), filters.q ? 350 : 0);
    return () => { inactive = true; window.clearTimeout(timer); };
  }, [fetchBusinesses, filters.q, refreshKey]);

  const archiveBusiness = async business => {
    const reason = window.prompt(`“${business.business_name}” संग्रहित करण्याचे कारण / Archive reason:`);
    if (reason === null) return;
    if (reason.trim().length < 3) {
      setError('संग्रहित करण्यासाठी किमान 3 अक्षरांचे कारण आवश्यक आहे.');
      return;
    }
    setArchivingId(business.business_uid);
    setError('');
    try {
      await businessAPI.archive(tenantSlug, business.business_uid, {
        version: business.version,
        reason: reason.trim(),
      });
      setRefreshKey(value => value + 1);
    } catch (requestError) {
      setError(errorMessage(requestError, 'व्यवसाय संग्रहित करता आला नाही.'));
    } finally {
      setArchivingId(null);
    }
  };

  const approveBusiness = async business => {
    setApprovingId(business.business_uid);
    setError('');
    try {
      await businessAPI.approveBusiness(tenantSlug, business.business_uid, {
        version: business.version,
        reason: 'प्रशासकीय मंजुरी',
      });
      setRefreshKey(value => value + 1);
    } catch (requestError) {
      setError(errorMessage(requestError, 'व्यवसाय मंजूर करता आला नाही.'));
    } finally {
      setApprovingId(null);
    }
  };

  const exportCsv = () => {
    const cell = value => `"${String(value ?? '').replace(/"/g, '""')}"`;
    const rows = [
      ['Business UID', 'Display name', 'Legal name', 'Constitution', 'Type', 'Registration', 'Status', 'Tax ready'],
      ...businesses.map(item => [
        item.business_uid, item.business_name, item.legal_name, item.constitution_type,
        item.business_type, item.registration_number, item.verification_status,
        item.tax_readiness?.ready ? 'Yes' : 'No',
      ]),
    ];
    const href = URL.createObjectURL(new Blob(
      ['\ufeff', rows.map(row => row.map(cell).join(',')).join('\n')],
      { type: 'text/csv;charset=utf-8' }
    ));
    const link = document.createElement('a');
    link.href = href;
    link.download = `business-registry-page-${page}-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(href);
  };

  const totalPages = Math.max(1, pagination.totalPages || 1);

  return (
    <div className="admin-list-container business-page">
      <div className="list-header business-list-heading">
        <div>
          <h1>🏪 व्यवसाय नोंदणी / Business Registry</h1>
          <p>कायदेशीर ओळख, मालकी, ठिकाणे आणि कर-पात्रता एकाच नोंदीत व्यवस्थापित करा.</p>
        </div>
        {canCreate && <Link to={`/admin/${tenantSlug}/businesses/new`} className="btn-primary">+ व्यवसाय नोंदवा</Link>}
      </div>

      <div className="business-table-card">
        <div className="business-table-toolbar business-registry-toolbar">
          <label className="business-search">
            <span aria-hidden="true">⌕</span>
            <span className="sr-only">व्यवसाय शोधा</span>
            <input
              placeholder="नाव, मालक, फोन किंवा नोंदणी क्रमांक..."
              value={filters.q}
              onChange={event => updateFilter('q', event.target.value)}
            />
          </label>
          <label>
            <span className="sr-only">व्यवसाय प्रकार</span>
            <select value={filters.type} onChange={event => updateFilter('type', event.target.value)}>
              <option value="">सर्व प्रकार / All types</option>
              {BUSINESS_TYPES.map(type => <option key={type} value={type}>{TYPE_LABELS[type]}</option>)}
            </select>
          </label>
          <label>
            <span className="sr-only">पडताळणी स्थिती</span>
            <select value={filters.status} onChange={event => updateFilter('status', event.target.value)}>
              <option value="">सर्व स्थिती / All statuses</option>
              {STATUS_FILTERS.map(status => <option key={status} value={status}>{STATUS_LABELS[status]}</option>)}
            </select>
          </label>
          <label>
            <span className="sr-only">कर पात्रता</span>
            <select value={filters.tax_readiness} onChange={event => updateFilter('tax_readiness', event.target.value)}>
              <option value="">सर्व कर-पात्रता</option>
              <option value="ready">करासाठी तयार / Ready</option>
              <option value="needs_review">तपासणी आवश्यक</option>
            </select>
          </label>
          <button type="button" className="business-export-btn" onClick={exportCsv} disabled={!businesses.length}>⇩ CSV</button>
        </div>

        <div className="business-table-meta" aria-live="polite">
          <span><strong>{pagination.total}</strong> नोंदी · पृष्ठ {page} / {totalPages}</span>
          <span className={`business-load-status ${loading ? 'visible' : ''}`}><i /> माहिती अद्ययावत होत आहे</span>
        </div>

        {error && <div className="business-alert error" role="alert">{error}</div>}

        <div className={`table-scroll-wrapper business-table-scroll ${loading ? 'is-refreshing' : ''}`}>
          <table className="admin-table business-data-table">
            <thead><tr>
              <th>व्यवसाय / Business</th><th>रचना व प्रकार</th><th>प्राथमिक मालक</th>
              <th>पडताळणी</th><th>कर पात्रता</th><th>आवृत्ती</th><th>कृती</th>
            </tr></thead>
            <tbody>
              {loading ? (
                <LoadingSpinner.TableRow colSpan={7} message="व्यवसाय माहिती लोड होत आहे..." />
              ) : businesses.length === 0 ? (
                <tr><td colSpan="7" className="business-empty">या फिल्टरसाठी व्यवसाय सापडले नाहीत.</td></tr>
              ) : (
                businesses.map(item => {
                const primary = (item.party_roles || []).find(role => role.status === 'active' && role.is_primary);
                return (
                  <tr key={item.business_uid}>
                    <td>
                      <div className="business-name-cell"><span>{String(item.business_name || 'व').charAt(0)}</span><div>
                        <strong>{item.business_name}</strong>
                        <small>{item.legal_name || item.registration_number || item.business_uid}</small>
                      </div></div>
                    </td>
                    <td><span className="business-type">{TYPE_LABELS[item.business_type] || item.business_type}</span><small className="business-secondary">{item.constitution_type?.replaceAll('_', ' ')}</small></td>
                    <td>{primary?.party?.display_name || primary?.party?.legal_name || item.owner_name || '—'}</td>
                    <td><span className={`business-verify ${item.verification_status}`}>{STATUS_LABELS[item.verification_status] || item.verification_status}</span></td>
                    <td><span className={`business-readiness ${item.tax_readiness?.ready ? 'ready' : 'review'}`}>{item.tax_readiness?.ready ? '✓ तयार' : `⚠ तपासा (${item.tax_readiness?.missing?.length || 0})`}</span></td>
                    <td>v{item.version}</td>
                    <td><div className="business-actions">
                      <button type="button" className="business-view-btn" onClick={() => setSelectedBusiness(item)}>तपशील</button>
                      {canManage && (item.verification_status === 'draft' || item.verification_status === 'pending') && (
                        <button
                          type="button"
                          className="business-approve-btn"
                          style={{ background: '#059669', color: '#ffffff', border: 'none', padding: '5px 9px', borderRadius: '4px', fontWeight: 700, fontSize: '12px', cursor: 'pointer' }}
                          disabled={approvingId === item.business_uid}
                          onClick={() => approveBusiness(item)}
                          title="तातडीने मंजूर व कर-पात्र करा"
                        >
                          {approvingId === item.business_uid ? '...' : '✓ मंजूर'}
                        </button>
                      )}
                      {canManage && <button type="button" className="business-edit-btn" onClick={() => navigate(`/admin/${tenantSlug}/businesses/${item.business_uid}`)}>व्यवस्थापन</button>}
                      {canArchive && item.verification_status !== 'archived' && <button type="button" className="business-delete-btn" disabled={archivingId === item.business_uid} onClick={() => archiveBusiness(item)}>{archivingId === item.business_uid ? '...' : 'संग्रहित'}</button>}
                    </div></td>
                  </tr>
                );
              })
            )}
            </tbody>
          </table>
        </div>

        <div className="table-pagination business-pagination">
          <label>प्रति पृष्ठ <select value={pageSize} onChange={event => { setPageSize(Number(event.target.value)); setPage(1); }}><option>10</option><option>20</option><option>50</option><option>100</option></select></label>
          <button className="btn btn-outline btn-sm" disabled={page <= 1 || loading} onClick={() => setPage(value => Math.max(1, value - 1))}>← मागील</button>
          <span>पृष्ठ <strong>{page}</strong> / {totalPages}</span>
          <button className="btn btn-outline btn-sm" disabled={page >= totalPages || loading} onClick={() => setPage(value => Math.min(totalPages, value + 1))}>पुढील →</button>
        </div>
      </div>

      {selectedBusiness && (
        <BusinessDetailModal
          tenantSlug={tenantSlug}
          business={selectedBusiness}
          onClose={() => setSelectedBusiness(null)}
          onManage={canManage
            ? () => navigate(`/admin/${tenantSlug}/businesses/${selectedBusiness.business_uid}`)
            : null}
        />
      )}
    </div>
  );
}
